// SelectDatasetModal.tsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Modal, Button, Form, Row, Col, Badge, Card, Dropdown, ButtonGroup } from 'react-bootstrap'
import { useGetContentMetadataQuery } from '../../services/contentApi'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { selectSelectedProjectId } from '../../store/slices/projectSlice'
import {
  selectDatasetSources,
  setDatasetSources,
  clearDatasetSources,
} from '../../store/slices/datasetSlice'
import { useGetProjectQuery } from '../../services/projectsApi'
import { useDatasetStats, type DatasetStats } from '../../utils/datasetStats'
import {
  Search,
  Database,
  Tag,
  Image,
  Layers,
  Check,
  Grid,
  List,
  Filter,
  ArrowUpDown,
  BarChart3,
  Package,
} from 'lucide-react'
import styled from 'styled-components'
import { isEqual } from 'lodash'

const StyledModal = styled(Modal)`
  .modal-content {
    background: #1a1a1a;
    border: none;
    max-height: 95vh;
  }

  .modal-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-bottom: none;
    padding: 1.5rem 2rem;

    .modal-title {
      color: white;
      font-weight: 700;
      font-size: 1.5rem;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }
  }

  .modal-body {
    background: #1a1a1a;
    padding: 0;
    max-height: 80vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
`

const CatalogHeader = styled.div`
  background: #2a2a2a;
  padding: 2rem;
  border-bottom: 1px solid #404040;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  .catalog-title {
    font-size: 1.75rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 0.5rem;
  }

  .catalog-subtitle {
    color: #b0b0b0;
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }
`

const FilterBar = styled.div`
  background: #2a2a2a;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #404040;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;

  .filter-section {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .view-controls {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
`

const SearchContainer = styled.div`
  position: relative;
  min-width: 320px;

  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #b0b0b0;
  }

  .form-control {
    padding-left: 2.75rem;
    height: 46px;
    background: #1a1a1a;
    border: 2px solid #404040;
    border-radius: 12px;
    color: #ffffff;
    font-size: 1rem;

    &:focus {
      background: #333333;
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
      color: #ffffff;
    }

    &::placeholder {
      color: #808080;
    }
  }
`

const FilterChips = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`

const FilterChip = styled.button<{ $isActive: boolean }>`
  background: ${(props) => (props.$isActive ? '#667eea' : '#1a1a1a')};
  color: ${(props) => (props.$isActive ? 'white' : '#b0b0b0')};
  border: 2px solid ${(props) => (props.$isActive ? '#667eea' : '#404040')};
  border-radius: 25px;
  padding: 0.5rem 1rem;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${(props) => (props.$isActive ? '#5a6fd8' : '#333333')};
    border-color: ${(props) => (props.$isActive ? '#5a6fd8' : '#667eea')};
    color: ${(props) => (props.$isActive ? 'white' : '#ffffff')};
  }
`

const ViewToggle = styled(ButtonGroup)`
  .btn {
    background: #1a1a1a;
    border: 2px solid #404040;
    color: #b0b0b0;
    padding: 0.5rem 1rem;

    &.active {
      background: #667eea;
      border-color: #667eea;
      color: white;
    }

    &:hover {
      background: #333333;
      border-color: #667eea;
      color: #ffffff;
    }
  }
`

const CatalogContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  background: #1a1a1a;
`

const DatasetCard = styled(Card)<{ $isSelected: boolean; $viewMode: string }>`
  background: #2a2a2a;
  border: 2px solid ${(props) => (props.$isSelected ? '#667eea' : '#404040')};
  border-radius: 16px;
  transition: all 0.3s ease;
  height: ${(props) => (props.$viewMode === 'list' ? 'auto' : '100%')};
  cursor: pointer;
  overflow: hidden;
  position: relative;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.4);
    border-color: ${(props) => (props.$isSelected ? '#5a6fd8' : '#667eea')};
  }

  ${(props) =>
    props.$viewMode === 'list' &&
    `
    .card-body {
      padding: 1.5rem;
    }
    
    .dataset-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 1rem;
    }
    
    .dataset-stats {
      display: flex;
      gap: 2rem;
      margin-top: 1rem;
    }
  `}

  .selected-indicator {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);
    z-index: 2;
  }

  .dataset-icon {
    position: absolute;
    top: 16px;
    left: 16px;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);
  }
`

const CardHeader = styled.div<{ $viewMode: string }>`
  background: linear-gradient(135deg, #333333 0%, #404040 100%);
  padding: ${(props) => (props.$viewMode === 'list' ? '1rem 1.5rem' : '4rem 1.5rem 1rem')};
  border-bottom: 1px solid #505050;
  position: relative;

  .dataset-name {
    font-size: 1.25rem;
    font-weight: 700;
    color: #ffffff;
    margin: 0 0 0.5rem 0;
    ${(props) =>
      props.$viewMode === 'grid' &&
      `
      margin-left: 3.5rem;
    `}
  }

  .dataset-source {
    font-size: 0.9rem;
    color: #b0b0b0;
    ${(props) =>
      props.$viewMode === 'grid' &&
      `
      margin-left: 3.5rem;
    `}
  }
`

const StatGrid = styled.div<{ $viewMode: string }>`
  display: ${(props) => (props.$viewMode === 'list' ? 'flex' : 'grid')};
  ${(props) =>
    props.$viewMode === 'grid' &&
    `
    grid-template-columns: 1fr 1fr;
  `}
  gap: ${(props) => (props.$viewMode === 'list' ? '2rem' : '1rem')};
  ${(props) =>
    props.$viewMode === 'list' &&
    `
    margin-top: 1rem;
  `}
`

const StatItem = styled.div<{ $viewMode: string }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  ${(props) =>
    props.$viewMode === 'list' &&
    `
    flex-direction: column;
    text-align: center;
  `}

  .stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, #667eea30 0%, #764ba230 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #667eea;
  }

  .stat-content {
    ${(props) =>
      props.$viewMode === 'list' &&
      `
      text-align: center;
    `}
  }

  .stat-label {
    font-size: 0.85rem;
    color: #b0b0b0;
    font-weight: 500;
    margin-bottom: 0.25rem;
  }

  .stat-value {
    font-size: 1.1rem;
    font-weight: 700;
    color: #ffffff;
  }
`

const TypeBadge = styled(Badge)<{ $type: string }>`
  background: ${(props) =>
    props.$type === 'training'
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : 'linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%)'};
  color: white;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`

const SelectionSummary = styled.div`
  background: linear-gradient(135deg, #56ab2f20 0%, #a8e6cf20 100%);
  border: 2px solid #56ab2f60;
  color: #a8e6cf;
  border-radius: 16px;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);

  .selection-text {
    font-weight: 600;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .clear-btn {
    background: #56ab2f30;
    color: #a8e6cf;
    border: 1px solid #56ab2f60;
    border-radius: 8px;
    padding: 0.5rem 1rem;
    font-weight: 600;
    transition: all 0.2s;

    &:hover {
      background: #56ab2f40;
      border-color: #56ab2f80;
      color: #ffffff;
    }
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #b0b0b0;

  .empty-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 1.5rem;
    opacity: 0.3;
  }

  .empty-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: #ffffff;
  }

  .empty-subtitle {
    font-size: 1.1rem;
  }
`

const ResultsInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  color: #b0b0b0;
  font-size: 0.95rem;

  .results-count {
    font-weight: 600;
    color: #ffffff;
  }
`

const ActionBar = styled.div`
  background: #2a2a2a;
  padding: 1.5rem 2rem;
  border-top: 1px solid #404040;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.3);
`

const InfoMessage = styled.div`
  background: linear-gradient(135deg, #667eea20 0%, #764ba220 100%);
  border: 1px solid #667eea60;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  color: #b3c3ff;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 500;

  svg {
    color: #667eea;
  }
`

interface DatasetCardProps {
  name: string
  datasetSource: string
  isSelected: boolean
  onClick: () => void
  stats?: DatasetStats
  singleSelect: boolean
  viewMode: 'grid' | 'list'
}

const DatasetCardComponent: React.FC<DatasetCardProps> = ({
  name,
  datasetSource,
  isSelected,
  onClick,
  stats,
  singleSelect,
  viewMode,
}) => {
  const isTraining = datasetSource.endsWith(':t')
  const totalImages = (stats?.total_unannotated_images || 0) + (stats?.total_annotated_images || 0)
  const classCount = Object.keys(stats?.annotations_per_class || {}).length
  const annotationCount = stats?.total_annotations || 0

  return (
    <DatasetCard $isSelected={isSelected} $viewMode={viewMode} onClick={onClick}>
      {isSelected && (
        <div className="selected-indicator">
          <Check size={18} />
        </div>
      )}

      {viewMode === 'grid' && (
        <div className="dataset-icon">
          <Database size={24} />
        </div>
      )}

      <CardHeader $viewMode={viewMode}>
        {viewMode === 'list' && (
          <div className="dataset-header">
            <div>
              <h5 className="dataset-name">{name}</h5>
              <div className="dataset-source">Source: {datasetSource}</div>
            </div>
            <TypeBadge $type={isTraining ? 'training' : 'validation'}>
              {isTraining ? 'Training' : 'Validation'}
            </TypeBadge>
          </div>
        )}

        {viewMode === 'grid' && (
          <>
            <h5 className="dataset-name">{name}</h5>
            <div className="dataset-source">Source: {datasetSource}</div>
            <div style={{ position: 'absolute', top: '1rem', right: '4rem' }}>
              <TypeBadge $type={isTraining ? 'training' : 'validation'}>
                {isTraining ? 'Training' : 'Validation'}
              </TypeBadge>
            </div>
          </>
        )}
      </CardHeader>

      <Card.Body>
        <StatGrid $viewMode={viewMode}>
          <StatItem $viewMode={viewMode}>
            <div className="stat-icon">
              <Tag size={20} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Classes</div>
              <div className="stat-value">{classCount}</div>
            </div>
          </StatItem>

          <StatItem $viewMode={viewMode}>
            <div className="stat-icon">
              <Image size={20} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Images</div>
              <div className="stat-value">{totalImages.toLocaleString()}</div>
            </div>
          </StatItem>

          <StatItem $viewMode={viewMode}>
            <div className="stat-icon">
              <Layers size={20} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Annotations</div>
              <div className="stat-value">{annotationCount.toLocaleString()}</div>
            </div>
          </StatItem>

          <StatItem $viewMode={viewMode}>
            <div className="stat-icon">
              <BarChart3 size={20} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Completeness</div>
              <div className="stat-value">
                {totalImages > 0
                  ? Math.round(((stats?.total_annotated_images || 0) / totalImages) * 100)
                  : 0}
                %
              </div>
            </div>
          </StatItem>
        </StatGrid>
      </Card.Body>
    </DatasetCard>
  )
}

interface SelectDatasetModalProps {
  show?: boolean
  singleSelect?: boolean
  canCreate?: boolean
  onHide: () => void
  handleAcceptSelections: (sources: string[]) => void
  selectedSources?: string[]
  updateGlobalState?: boolean // Add prop to control global state updates
}

const SelectDatasetModal: React.FC<SelectDatasetModalProps> = ({
  show = false,
  singleSelect = false,
  canCreate = true,
  onHide,
  handleAcceptSelections,
  selectedSources = [],
  updateGlobalState = true, // Default to true for backwards compatibility
}) => {
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  // const selectedDatasets = useAppSelector(selectDatasetSources)
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([])

  const { data: metadata } = useGetContentMetadataQuery(
    {
      project_id: selectedProjectId || '',
    },
    { skip: !selectedProjectId }
  )

  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  // Get statistics for all available datasets
  const availableDatasets = useMemo(() => metadata?.metadata?.datasource || [], [metadata])
  const datasetStats = useDatasetStats(
    selectedProjectId || '',
    project?.datasetid || '',
    availableDatasets
  )

  // State
  const [searchTerm, setSearchTerm] = useState('')
  const [purposeFilter, setPurposeFilter] = useState('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [sortBy, setSortBy] = useState('name')
  const prevSelectedSourcesRef = useRef<string[]>([])

  // Memoize the filtered and sorted datasets
  const filteredAndSortedDatasets = useMemo(() => {
    const filtered = availableDatasets.filter((dataset) => {
      const matchesSearch = dataset.toLowerCase().includes(searchTerm.toLowerCase())
      if (purposeFilter === 'all') return matchesSearch
      const matchesPurpose = dataset.endsWith(purposeFilter.toLowerCase().slice(0, 1))
      return matchesSearch && matchesPurpose
    })

    // Sort datasets
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.split(':')[0].localeCompare(b.split(':')[0])
        case 'type':
          return a.endsWith(':t') === b.endsWith(':t') ? 0 : a.endsWith(':t') ? -1 : 1
        case 'images':
          const aImages =
            (datasetStats[a]?.total_unannotated_images || 0) +
            (datasetStats[a]?.total_annotated_images || 0)
          const bImages =
            (datasetStats[b]?.total_unannotated_images || 0) +
            (datasetStats[b]?.total_annotated_images || 0)
          return bImages - aImages
        case 'annotations':
          return (
            (datasetStats[b]?.total_annotations || 0) - (datasetStats[a]?.total_annotations || 0)
          )
        default:
          return 0
      }
    })

    return filtered
  }, [availableDatasets, purposeFilter, searchTerm, sortBy, datasetStats])

  // Initialize selected datasets when component mounts or when availableDatasets change
  // REMOVED: This was causing unwanted automatic dataset selection when switching tabs
  // useEffect(() => {
  //   if (availableDatasets.length > 0 && selectedDatasets.length === 0) {
  //     const sourcesToSet = selectedSources.length > 0 ? selectedSources : availableDatasets
  //     dispatch(setDatasetSources(sourcesToSet))
  //   }
  // }, [availableDatasets, selectedSources, dispatch])

  // Update selections when selectedSources prop changes
  useEffect(() => {
    if (selectedSources.length > 0 && !isEqual(selectedSources, prevSelectedSourcesRef.current)) {
      prevSelectedSourcesRef.current = selectedSources
      // if (updateGlobalState) {
      //   dispatch(setDatasetSources(selectedSources))
      // }
    }
  }, [selectedSources, dispatch, updateGlobalState])

  // Handlers
  const handleToggleSelection = useCallback(
    (datasetSource: string) => {
      if (singleSelect) {
        // dispatch(setDatasetSources([datasetSource]))
        return
      }

      const isSelected = selectedDatasets.includes(datasetSource)
      if (isSelected) {
        // dispatch(setDatasetSources(selectedDatasets.filter((ds) => ds !== datasetSource)))
        setSelectedDatasets(selectedDatasets.filter((ds) => ds !== datasetSource))
      } else {
        // dispatch(setDatasetSources([...selectedDatasets, datasetSource]))
        setSelectedDatasets([...selectedDatasets, datasetSource])
      }
    },
    [singleSelect, selectedDatasets]
  )

  const handleClearSelection = useCallback(() => {
    setSelectedDatasets([])
  }, [])

  const handleSelectAll = useCallback(() => {
    if (!singleSelect) {
      setSelectedDatasets(filteredAndSortedDatasets)
    }
  }, [singleSelect, filteredAndSortedDatasets])

  const handleAccept = useCallback(() => {
    handleAcceptSelections(selectedDatasets)
    onHide()
  }, [handleAcceptSelections, selectedDatasets, onHide])

  return (
    <StyledModal size="xl" show={show} onHide={onHide} centered>
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title className="d-flex align-items-center gap-2">
          <Package size={24} />
          Dataset Catalog
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <CatalogHeader>
          <h2 className="catalog-title">
            {singleSelect ? 'Select a Dataset' : 'Browse & Select Datasets'}
          </h2>
          <p className="catalog-subtitle">
            Explore and choose from {availableDatasets.length} available datasets in your project
          </p>
        </CatalogHeader>

        <FilterBar>
          <div className="filter-section">
            <SearchContainer>
              <Search size={18} className="search-icon" />
              <Form.Control
                type="text"
                placeholder="Search datasets by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchContainer>

            <FilterChips>
              <FilterChip
                $isActive={purposeFilter === 'all'}
                onClick={() => setPurposeFilter('all')}
                type="button"
              >
                <Filter size={14} />
                All Types
              </FilterChip>
              <FilterChip
                $isActive={purposeFilter === 'training'}
                onClick={() => setPurposeFilter('training')}
                type="button"
              >
                Training
              </FilterChip>
              <FilterChip
                $isActive={purposeFilter === 'validation'}
                onClick={() => setPurposeFilter('validation')}
                type="button"
              >
                Validation
              </FilterChip>
            </FilterChips>

            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" size="sm">
                <ArrowUpDown size={14} className="me-1" />
                Sort by {sortBy}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setSortBy('name')}>Name</Dropdown.Item>
                <Dropdown.Item onClick={() => setSortBy('type')}>Type</Dropdown.Item>
                <Dropdown.Item onClick={() => setSortBy('images')}>Image Count</Dropdown.Item>
                <Dropdown.Item onClick={() => setSortBy('annotations')}>Annotations</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <div className="view-controls">
            {!singleSelect && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleSelectAll}
                disabled={
                  filteredAndSortedDatasets.length === 0 ||
                  filteredAndSortedDatasets.every((d) => selectedDatasets.includes(d))
                }
              >
                Select All
              </Button>
            )}

            <ViewToggle>
              <Button
                variant={viewMode === 'grid' ? 'primary' : 'outline-secondary'}
                onClick={() => setViewMode('grid')}
              >
                <Grid size={16} />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'primary' : 'outline-secondary'}
                onClick={() => setViewMode('list')}
              >
                <List size={16} />
              </Button>
            </ViewToggle>
          </div>
        </FilterBar>

        <CatalogContent>
          {selectedDatasets.length > 0 && (
            <SelectionSummary>
              <div className="selection-text">
                <Check size={18} />
                <strong>{selectedDatasets.length}</strong> dataset
                {selectedDatasets.length !== 1 ? 's' : ''} selected
              </div>
              <Button onClick={handleClearSelection} className="clear-btn">
                Clear Selection
              </Button>
            </SelectionSummary>
          )}

          <ResultsInfo>
            <span>
              Showing <span className="results-count">{filteredAndSortedDatasets.length}</span> of{' '}
              <span className="results-count">{availableDatasets.length}</span> datasets
            </span>
          </ResultsInfo>

          {filteredAndSortedDatasets.length > 0 ? (
            <Row
              xs={1}
              md={viewMode === 'grid' ? 2 : 1}
              lg={viewMode === 'grid' ? 3 : 1}
              className="g-4"
            >
              {filteredAndSortedDatasets.map((dataset) => {
                const name = dataset.split(':')[0]

                return (
                  <Col key={dataset}>
                    <DatasetCardComponent
                      name={name}
                      datasetSource={dataset}
                      isSelected={selectedDatasets.includes(dataset)}
                      onClick={() => handleToggleSelection(dataset)}
                      stats={datasetStats[dataset]}
                      singleSelect={singleSelect}
                      viewMode={viewMode}
                    />
                  </Col>
                )
              })}
            </Row>
          ) : (
            <EmptyState>
              <Database className="empty-icon" />
              <h3 className="empty-title">No datasets found</h3>
              <p className="empty-subtitle">
                No datasets match your current search and filter criteria.
              </p>
            </EmptyState>
          )}
        </CatalogContent>

        <ActionBar>
          {selectedDatasets.length === 0 && !singleSelect && (
            <InfoMessage>
              <Database size={16} />
              No datasets selected - all available data will be shown
            </InfoMessage>
          )}
          {(selectedDatasets.length > 0 || singleSelect) && <div></div>}

          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAccept}
              disabled={singleSelect && selectedDatasets.length === 0}
            >
              {singleSelect ? 'Select Dataset' : 'Apply Selection'}
            </Button>
          </div>
        </ActionBar>
      </Modal.Body>
    </StyledModal>
  )
}

export default SelectDatasetModal
